/**
 * Question Editor Component
 *
 * Provides the editing interface for a single question.
 * Includes settings for:
 * - Question text and description
 * - Required toggle
 * - Validation rules
 * - Options (for choice questions)
 * - Branching logic
 * - Rating visibility settings
 */

import { useState } from 'react';
import {
  Plus,
  Trash2,
  GripVertical,
  AlertCircle,
  Sparkles,
  ArrowRight,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import {
  getQuestionTypeInfo,
  QuestionOption,
  ValidationRules,
  BranchingRule,
} from '@/lib/forms/question-types';
import { FormQuestion } from './hooks/useFormBuilder';

interface QuestionEditorProps {
  question: FormQuestion;
  allQuestions: FormQuestion[];
  onUpdate: (updates: Partial<FormQuestion>) => void;
  onAddOption: () => void;
  onUpdateOption: (optionId: string, updates: Partial<QuestionOption>) => void;
  onDeleteOption: (optionId: string) => void;
  onAddBranchingRule: (rule: BranchingRule) => void;
  onUpdateBranchingRule: (index: number, updates: Partial<BranchingRule>) => void;
  onDeleteBranchingRule: (index: number) => void;
}

export function QuestionEditor({
  question,
  allQuestions,
  onUpdate,
  onAddOption,
  onUpdateOption,
  onDeleteOption,
  onAddBranchingRule,
  onUpdateBranchingRule,
  onDeleteBranchingRule,
}: QuestionEditorProps) {
  const [showValidation, setShowValidation] = useState(false);
  const [showBranching, setShowBranching] = useState(
    (question.branchingRules?.length || 0) > 0
  );

  const typeInfo = getQuestionTypeInfo(question.type);

  // Other questions for branching targets
  const otherQuestions = allQuestions.filter(q => q.id !== question.id);

  return (
    <div className="space-y-6">
      {/* Basic Settings */}
      <div className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="question-text">Question</Label>
          <Textarea
            id="question-text"
            value={question.text}
            onChange={e => onUpdate({ text: e.target.value })}
            placeholder="Enter your question..."
            className="resize-none"
            rows={2}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="question-description">Description (optional)</Label>
          <Input
            id="question-description"
            value={question.uiConfig?.description || ''}
            onChange={e =>
              onUpdate({
                uiConfig: { ...question.uiConfig, description: e.target.value },
              })
            }
            placeholder="Add a description or help text..."
          />
        </div>

        {/* Placeholder for text inputs */}
        {['short_text', 'long_text', 'email', 'phone', 'number', 'url'].includes(
          question.type
        ) && (
          <div className="space-y-2">
            <Label htmlFor="question-placeholder">Placeholder</Label>
            <Input
              id="question-placeholder"
              value={question.placeholder || ''}
              onChange={e => onUpdate({ placeholder: e.target.value })}
              placeholder="Type your answer here..."
            />
          </div>
        )}

        {/* Required Toggle */}
        <div className="flex items-center justify-between">
          <Label htmlFor="question-required" className="cursor-pointer">
            Required
          </Label>
          <Switch
            id="question-required"
            checked={question.required}
            onCheckedChange={required => onUpdate({ required })}
          />
        </div>
      </div>

      {/* Options Editor (for choice questions) */}
      {typeInfo?.supportsOptions && (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <Label>Options</Label>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={onAddOption}
              className="h-7 text-xs"
            >
              <Plus className="h-3 w-3 mr-1" />
              Add Option
            </Button>
          </div>

          <div className="space-y-2">
            {question.options?.map((option, index) => (
              <div
                key={option.id}
                className="flex items-center gap-2 p-2 rounded-lg border bg-muted/30"
              >
                <div className="flex-shrink-0 text-muted-foreground cursor-grab">
                  <GripVertical className="h-4 w-4" />
                </div>

                <div className="w-6 h-6 rounded-full bg-muted flex items-center justify-center flex-shrink-0">
                  <span className="text-xs font-medium">
                    {String.fromCharCode(65 + index)}
                  </span>
                </div>

                <Input
                  value={option.label}
                  onChange={e =>
                    onUpdateOption(option.id, {
                      label: e.target.value,
                      value: e.target.value.toLowerCase().replace(/\s+/g, '_'),
                    })
                  }
                  placeholder="Option label"
                  className="flex-1 h-8"
                />

                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => onDeleteOption(option.id)}
                  className="h-8 w-8 p-0 text-muted-foreground hover:text-destructive"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            ))}

            {(!question.options || question.options.length === 0) && (
              <div className="text-sm text-muted-foreground text-center py-4 border rounded-lg border-dashed">
                No options yet. Click "Add Option" to create some.
              </div>
            )}
          </div>

          {/* Show letter indicators toggle */}
          <div className="flex items-center justify-between">
            <Label
              htmlFor="show-letters"
              className="text-sm text-muted-foreground cursor-pointer"
            >
              Show letter indicators (A, B, C...)
            </Label>
            <Switch
              id="show-letters"
              checked={question.uiConfig?.showLetterIndicators ?? true}
              onCheckedChange={show =>
                onUpdate({
                  uiConfig: { ...question.uiConfig, showLetterIndicators: show },
                })
              }
            />
          </div>
        </div>
      )}

      {/* Opinion Scale Settings */}
      {question.type === 'opinion_scale' && (
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="scale-min">Minimum Value</Label>
              <Input
                id="scale-min"
                type="number"
                value={question.validationRules?.min ?? 1}
                onChange={e =>
                  onUpdate({
                    validationRules: {
                      ...question.validationRules,
                      min: parseInt(e.target.value) || 1,
                    },
                  })
                }
                min={0}
                max={10}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="scale-max">Maximum Value</Label>
              <Input
                id="scale-max"
                type="number"
                value={question.validationRules?.max ?? 10}
                onChange={e =>
                  onUpdate({
                    validationRules: {
                      ...question.validationRules,
                      max: parseInt(e.target.value) || 10,
                    },
                  })
                }
                min={1}
                max={10}
              />
            </div>
          </div>
        </div>
      )}

      {/* Rating Visibility */}
      <div className="space-y-3">
        <Label className="text-sm text-muted-foreground">Show this question for:</Label>
        <div className="flex gap-4">
          <label className="flex items-center gap-2 cursor-pointer">
            <Switch
              checked={question.visibleOnHighRating ?? true}
              onCheckedChange={visible => onUpdate({ visibleOnHighRating: visible })}
            />
            <span className="text-sm">High ratings (4-5 stars)</span>
          </label>
          <label className="flex items-center gap-2 cursor-pointer">
            <Switch
              checked={question.visibleOnLowRating ?? true}
              onCheckedChange={visible => onUpdate({ visibleOnLowRating: visible })}
            />
            <span className="text-sm">Low ratings (1-3 stars)</span>
          </label>
        </div>
      </div>

      {/* Validation Rules */}
      <Collapsible open={showValidation} onOpenChange={setShowValidation}>
        <CollapsibleTrigger asChild>
          <Button variant="ghost" className="w-full justify-between">
            <span className="flex items-center gap-2">
              <AlertCircle className="h-4 w-4" />
              Validation Rules
            </span>
            <Badge variant="outline" className="ml-2">
              {showValidation ? 'Hide' : 'Show'}
            </Badge>
          </Button>
        </CollapsibleTrigger>
        <CollapsibleContent className="pt-4">
          <ValidationRulesEditor
            question={question}
            onUpdate={onUpdate}
          />
        </CollapsibleContent>
      </Collapsible>

      {/* Branching Logic */}
      {typeInfo?.supportsBranching && (
        <Collapsible open={showBranching} onOpenChange={setShowBranching}>
          <CollapsibleTrigger asChild>
            <Button variant="ghost" className="w-full justify-between">
              <span className="flex items-center gap-2">
                <Sparkles className="h-4 w-4" />
                Branching Logic
              </span>
              <Badge variant="outline" className="ml-2">
                {question.branchingRules?.length || 0} rules
              </Badge>
            </Button>
          </CollapsibleTrigger>
          <CollapsibleContent className="pt-4">
            <BranchingRulesEditor
              question={question}
              otherQuestions={otherQuestions}
              onAddRule={onAddBranchingRule}
              onUpdateRule={onUpdateBranchingRule}
              onDeleteRule={onDeleteBranchingRule}
            />
          </CollapsibleContent>
        </Collapsible>
      )}
    </div>
  );
}

// Validation Rules Sub-Editor
function ValidationRulesEditor({
  question,
  onUpdate,
}: {
  question: FormQuestion;
  onUpdate: (updates: Partial<FormQuestion>) => void;
}) {
  const updateValidation = (updates: Partial<ValidationRules>) => {
    onUpdate({
      validationRules: { ...question.validationRules, ...updates },
    });
  };

  return (
    <div className="space-y-4 p-4 rounded-lg border">
      {/* Text length validation */}
      {['short_text', 'long_text'].includes(question.type) && (
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="min-length">Min Length</Label>
            <Input
              id="min-length"
              type="number"
              value={question.validationRules?.minLength ?? ''}
              onChange={e =>
                updateValidation({
                  minLength: e.target.value ? parseInt(e.target.value) : undefined,
                })
              }
              placeholder="No minimum"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="max-length">Max Length</Label>
            <Input
              id="max-length"
              type="number"
              value={question.validationRules?.maxLength ?? ''}
              onChange={e =>
                updateValidation({
                  maxLength: e.target.value ? parseInt(e.target.value) : undefined,
                })
              }
              placeholder="No maximum"
            />
          </div>
        </div>
      )}

      {/* Number range validation */}
      {question.type === 'number' && (
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="min-value">Min Value</Label>
            <Input
              id="min-value"
              type="number"
              value={question.validationRules?.min ?? ''}
              onChange={e =>
                updateValidation({
                  min: e.target.value ? parseFloat(e.target.value) : undefined,
                })
              }
              placeholder="No minimum"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="max-value">Max Value</Label>
            <Input
              id="max-value"
              type="number"
              value={question.validationRules?.max ?? ''}
              onChange={e =>
                updateValidation({
                  max: e.target.value ? parseFloat(e.target.value) : undefined,
                })
              }
              placeholder="No maximum"
            />
          </div>
        </div>
      )}

      {/* Regex validation */}
      <div className="space-y-2">
        <Label htmlFor="regex">Custom Regex Pattern (advanced)</Label>
        <Input
          id="regex"
          value={question.validationRules?.regex ?? ''}
          onChange={e => updateValidation({ regex: e.target.value || undefined })}
          placeholder="^[a-zA-Z]+$"
          className="font-mono text-sm"
        />
        <p className="text-xs text-muted-foreground">
          Leave empty for default validation
        </p>
      </div>
    </div>
  );
}

// Branching Rules Sub-Editor
function BranchingRulesEditor({
  question,
  otherQuestions,
  onAddRule,
  onUpdateRule,
  onDeleteRule,
}: {
  question: FormQuestion;
  otherQuestions: FormQuestion[];
  onAddRule: (rule: BranchingRule) => void;
  onUpdateRule: (index: number, updates: Partial<BranchingRule>) => void;
  onDeleteRule: (index: number) => void;
}) {
  const handleAddRule = () => {
    onAddRule({
      conditionType: 'equals',
      conditionValue: '',
      targetQuestionId: otherQuestions[0]?.id || 'end',
    });
  };

  return (
    <div className="space-y-4 p-4 rounded-lg border">
      <p className="text-sm text-muted-foreground">
        Jump to a specific question based on the answer to this question.
      </p>

      {question.branchingRules?.map((rule, index) => (
        <div
          key={index}
          className="flex items-start gap-2 p-3 rounded-lg bg-muted/50"
        >
          <div className="flex-1 space-y-3">
            <div className="flex items-center gap-2 text-sm">
              <span>If answer</span>
              <Select
                value={rule.conditionType}
                onValueChange={(value: BranchingRule['conditionType']) =>
                  onUpdateRule(index, { conditionType: value })
                }
              >
                <SelectTrigger className="w-32 h-8">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="equals">equals</SelectItem>
                  <SelectItem value="contains">contains</SelectItem>
                  {question.type === 'number' ||
                    question.type === 'rating' ||
                    question.type === 'opinion_scale' ? (
                    <>
                      <SelectItem value="greater_than">greater than</SelectItem>
                      <SelectItem value="less_than">less than</SelectItem>
                    </>
                  ) : null}
                </SelectContent>
              </Select>

              {/* Value input */}
              {question.options && question.options.length > 0 ? (
                <Select
                  value={String(rule.conditionValue)}
                  onValueChange={value => onUpdateRule(index, { conditionValue: value })}
                >
                  <SelectTrigger className="w-40 h-8">
                    <SelectValue placeholder="Select option" />
                  </SelectTrigger>
                  <SelectContent>
                    {question.options.map(opt => (
                      <SelectItem key={opt.id} value={opt.value}>
                        {opt.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : (
                <Input
                  value={String(rule.conditionValue)}
                  onChange={e => onUpdateRule(index, { conditionValue: e.target.value })}
                  placeholder="Value"
                  className="w-32 h-8"
                />
              )}
            </div>

            <div className="flex items-center gap-2 text-sm">
              <ArrowRight className="h-4 w-4 text-muted-foreground" />
              <span>Then go to</span>
              <Select
                value={rule.targetQuestionId}
                onValueChange={value =>
                  onUpdateRule(index, { targetQuestionId: value })
                }
              >
                <SelectTrigger className="w-48 h-8">
                  <SelectValue placeholder="Select target" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="end">End of form</SelectItem>
                  <SelectItem value="google_redirect">
                    Google redirect (high rating)
                  </SelectItem>
                  {otherQuestions.map((q, qIndex) => (
                    <SelectItem key={q.id} value={q.id}>
                      Q{qIndex + 1}: {q.text.substring(0, 30)}
                      {q.text.length > 30 ? '...' : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => onDeleteRule(index)}
            className="h-8 w-8 p-0 text-muted-foreground hover:text-destructive"
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      ))}

      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={handleAddRule}
        className="w-full"
      >
        <Plus className="h-4 w-4 mr-2" />
        Add Branching Rule
      </Button>
    </div>
  );
}
